import { FC } from 'react';
import { useParams } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import api from 'lib/trpcClient';
import OrganisationDetails from './OrganisationDetails';
import LinkedAdvisers from './LinkedAdvisers';
import LinkedInvestments from './LinkedInvestments';
import OrganisationUsers from './OrganisationUsers';
import { useLinkedInvestments } from './hooks/useLinkedInvestments';
import { useLinkedAdvisers } from './hooks/useLinkedAdvisers';
import OrganisationSnapshot from './OrganisationSnapshot';
import PageContent from 'components/PageContent';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const breadcrumbs = [
  { label: 'Dashboard' },
  { label: 'Organisation', link: '/organisation' },
  {
    label: 'View Organisation',
    link: '/',
    isActive: true,
  },
];

const ViewOrganisation: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [canReadOrgUsers] = useHasPermissions([
    RolePermission.ReadOrganisationUsers,
  ]);
  const { data: organisation, isFetching } = api.organisation.getById.useQuery(
    { id: id ?? '' },
    { enabled: !!id },
  );
  const {
    handleSearch: handleSearchLinkedInvestments,
    pagination: linkedInvestmentsPagination,
    data: linkedInvestmentsData,
    isFetching: isFetchingLinkedInvestments,
  } = useLinkedInvestments(organisation?._id);

  const {
    handleSearch: handleSearchLinkedAdvisers,
    pagination: linkedAdvisersPagination,
    data: linkedAdvisersData,
    isFetching: isFetchingLinkedAdvisers,
  } = useLinkedAdvisers(organisation?._id);

  const snapshotTotals = {
    ...linkedInvestmentsData?.totals,
    totalAdvisers: linkedAdvisersData?.totalAdviser,
    childOrganisationsCount: organisation?.childOrganisationsCount,
  };

  const isLoadingSnapshot =
    isFetching || isFetchingLinkedAdvisers || isFetchingLinkedInvestments;

  return (
    <>
      <PageContainer
        heading="Organisation: View organisation"
        breadcrumbs={breadcrumbs}
        loading={isFetching}
      >
        <PageContent>
          <OrganisationDetails organisation={organisation} />
          {organisation && (
            <>
              <OrganisationSnapshot
                //@ts-expect-error
                totals={snapshotTotals}
                isParent={organisation?.type === 'parent'}
                isLoading={isLoadingSnapshot}
              />
              {canReadOrgUsers && (
                <OrganisationUsers organisationId={organisation?._id} />
              )}
              <LinkedAdvisers
                organisation={organisation}
                data={linkedAdvisersData}
                isFetching={isFetchingLinkedAdvisers}
                handleSearch={handleSearchLinkedAdvisers}
                pagination={linkedAdvisersPagination}
              />
              <LinkedInvestments
                linkedInvestments={{
                  //@ts-expect-error
                  data: linkedInvestmentsData,
                  isFetching: isFetchingLinkedInvestments,
                }}
                handleSearch={handleSearchLinkedInvestments}
                pagination={linkedInvestmentsPagination}
              />
            </>
          )}
        </PageContent>
      </PageContainer>
    </>
  );
};

export default ViewOrganisation;
